import {
  NozomiReconciliationSite,
  ReconciliationCounts,
  ReconciliationRow,
  RowFilter,
  RowTag,
} from '@totem/components/administration/reconciliation/nozomi/types';

// A sensor skipping at least this share of its assets is flagged.
const HIGH_SKIP_RATIO = 0.25;

const siteCounts = (site: NozomiReconciliationSite): ReconciliationCounts => ({
  assets: site.assets,
  matched: site.matched,
  created: site.created,
  notUnderContract: site.notUnderContract,
  noUniqueMac: site.noUniqueMac,
  notUnderContractNew: site.notUnderContractNew,
  multipleMatches: site.multipleMatches,
  noBuilding: site.noBuilding,
  otherSkipped: site.createFailed + site.creationDisabled,
  portalSynced: site.portalSynced,
  portalActive: site.portalActive,
  notSeen: site.notSeen,
  assetKeys: site.assetKeys,
  merged: site.merged,
  maxAssetsPerDevice: site.maxAssetsPerDevice,
  multiAssetDevices: site.multiAssetDevices,
  variance: site.variance,
  processed: site.processed,
});

export const sumCounts = (
  counts: ReconciliationCounts[],
): ReconciliationCounts => {
  const total: ReconciliationCounts = {
    assets: 0,
    matched: 0,
    created: 0,
    notUnderContract: 0,
    noUniqueMac: 0,
    notUnderContractNew: 0,
    multipleMatches: 0,
    noBuilding: 0,
    otherSkipped: 0,
    portalSynced: 0,
    portalActive: 0,
    notSeen: 0,
    assetKeys: 0,
    merged: 0,
    maxAssetsPerDevice: 0,
    multiAssetDevices: 0,
    variance: 0,
    processed: false,
  };

  counts.forEach((item) => {
    total.assets += item.assets;

    total.matched += item.matched;

    total.created += item.created;

    total.notUnderContract += item.notUnderContract;

    total.noUniqueMac += item.noUniqueMac;

    total.notUnderContractNew += item.notUnderContractNew;

    total.multipleMatches += item.multipleMatches;

    total.noBuilding += item.noBuilding;

    total.otherSkipped += item.otherSkipped;

    total.portalSynced += item.portalSynced;

    total.portalActive += item.portalActive;

    total.notSeen += item.notSeen;

    total.assetKeys += item.assetKeys;

    total.merged += item.merged;

    total.multiAssetDevices += item.multiAssetDevices;

    total.variance += item.variance;

    total.maxAssetsPerDevice = Math.max(
      total.maxAssetsPerDevice,
      item.maxAssetsPerDevice,
    );

    total.processed = total.processed || item.processed;
  });

  return total;
};

export const getSiteTags = (site: NozomiReconciliationSite): RowTag[] => {
  const tags: RowTag[] = [];

  if (site.status === 'Inactive') {
    tags.push({
      label: 'Inactive',
      color: 'default',
      tooltip: 'The sensor record is inactive, so the sync skips it.',
    });
  } else if (!site.mapped) {
    tags.push({
      label: 'Not mapped',
      color: 'default',
      tooltip:
        'No building is assigned to the sensor record, so the sync skips it. Map it on the API Sites page.',
    });
  } else if (!site.processed) {
    tags.push({
      label: 'Not in this run',
      color: 'orange',
      tooltip:
        'The sensor is mapped but the latest run logged no results for it.',
    });
  } else if (site.assets === 0) {
    tags.push({
      label: 'No assets',
      color: 'default',
      tooltip: 'Vantage returned no assets for this sensor in the latest run.',
    });
  }

  if (site.duplicateSensor) {
    tags.push({
      label: site.olderDuplicate ? 'Older duplicate' : 'Duplicate sensor',
      color: 'red',
      tooltip: site.olderDuplicate
        ? 'A newer mapped record has the same sensor name. Both pull the same assets; set this one to Inactive.'
        : 'Another mapped record has the same sensor name, so both pull the same assets.',
    });
  }

  if (site.assets > 0 && site.noUniqueMac / site.assets >= HIGH_SKIP_RATIO) {
    tags.push({
      label: `${Math.round((100 * site.noUniqueMac) / site.assets)}% no MAC`,
      color: 'gold',
      tooltip:
        'A large share of assets only have locally administered or randomized MACs, so they are not imported.',
    });
  }

  if (site.notUnderContract + site.notUnderContractNew > 0) {
    tags.push({
      label: 'No contract',
      color: 'gold',
      tooltip:
        'The building has no active Threat Detection or Asset Inventory line.',
    });
  }

  if (site.multipleMatches > 0) {
    tags.push({
      label: 'Duplicate devices',
      color: 'red',
      tooltip:
        'Some assets match more than one portal device. Merge the duplicates.',
    });
  }

  if (site.portalOtherOrg > 0 || site.orgMismatch > 0) {
    tags.push({
      label: 'Other organization',
      color: 'red',
      tooltip: `${site.portalOtherOrg} active devices linked to this sensor belong to another organization.`,
    });
  }

  if (site.createFailed > 0) {
    tags.push({
      label: 'Create failed',
      color: 'red',
      tooltip: `${site.createFailed} devices failed to create. See Log Sync for the error.`,
    });
  }

  return tags;
};

const needsAttention = (site: NozomiReconciliationSite, tags: RowTag[]) =>
  site.variance !== 0 ||
  tags.some((tag) => ['red', 'orange', 'gold'].includes(tag.color));

const hasActivity = (site: NozomiReconciliationSite) =>
  site.assets > 0 || site.portalActive > 0;

// Sensor coverage: a record the sync should process but either can't (no building), didn't
// (no log in the latest run), or got nothing back for (often a Guardian offline in Vantage).
export const isNotMapped = (site: NozomiReconciliationSite) =>
  site.status !== 'Inactive' && !site.mapped;

export const isNotSynced = (site: NozomiReconciliationSite) =>
  site.mapped && !site.processed;

export const hasNoAssets = (site: NozomiReconciliationSite) =>
  site.mapped && site.processed && site.assets === 0;

const matchesFilter = (
  site: NozomiReconciliationSite,
  tags: RowTag[],
  filter: RowFilter,
) => {
  switch (filter) {
    case 'withAssets':
      return hasActivity(site);
    case 'needsAttention':
      return needsAttention(site, tags);
    case 'notMapped':
      return isNotMapped(site);
    case 'notSynced':
      return isNotSynced(site);
    case 'noAssets':
      return hasNoAssets(site);
    default:
      return true;
  }
};

export const buildRows = (
  sites: NozomiReconciliationSite[],
  filter: RowFilter,
): ReconciliationRow[] => {
  const byOrganization = new Map<string, NozomiReconciliationSite[]>();
  sites.forEach((site) => {
    const orgKey = site.organization.id || site.organization.name;
    byOrganization.set(orgKey, [...(byOrganization.get(orgKey) ?? []), site]);
  });

  const rows: ReconciliationRow[] = [];
  byOrganization.forEach((orgSites, orgKey) => {
    const children = orgSites
      .map((site) => ({ site, tags: getSiteTags(site) }))
      .filter(({ site, tags }) => matchesFilter(site, tags, filter))
      .sort((left, right) => right.site.assets - left.site.assets)
      .map(({ site, tags }): ReconciliationRow => ({
        ...siteCounts(site),
        key: site.siteId,
        kind: 'sensor',
        name: site.siteName,
        building: site.building?.name || 'No building assigned',
        tags,
      }));

    if (children.length === 0) {
      return;
    }

    rows.push({
      ...sumCounts(orgSites.map(siteCounts)),
      key: `org-${orgKey}`,
      kind: 'organization',
      name: orgSites[0].organization.name || 'No organization',
      sensorCount: orgSites.filter((site) => site.mapped).length,
      tags: [],
      children,
    });
  });

  return rows.sort((left, right) => right.assets - left.assets);
};

export const getTotals = (sites: NozomiReconciliationSite[]) =>
  sumCounts(sites.map(siteCounts));
